import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import { AppText } from "@toothfairy/shared-ui";

const StructuredQuestionsInput = ({
  structuredQuestions,
  onSend,
  maxHeight,
  currentTheme,
  setIsExpanded,
}) => {
  const { t } = useTranslation();
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isInReviewMode, setIsInReviewMode] = useState(false);
  const [validationError, setValidationError] = useState(null);

  const questions = structuredQuestions?.questions || [];
  const totalQuestions = questions.length;

  const allQuestionsAnswered = useMemo(() => {
    return questions.every((q) => answers[q.question_id]);
  }, [questions, answers]);

  const handleOptionSelect = (option) => {
    const qId = questions[activeQuestionIndex].question_id;
    setAnswers((prev) => ({
      ...prev,
      [qId]: { type: "selection", value: option.value },
    }));
    setValidationError(null);
  };

  const handleCustomInput = (text) => {
    const qId = questions[activeQuestionIndex]?.question_id;
    if (text.trim()) {
      setAnswers((prev) => ({
        ...prev,
        [qId]: { type: "custom", value: text },
      }));
    } else {
      setAnswers((prev) => {
        const newAnswers = { ...prev };
        delete newAnswers[qId];
        return newAnswers;
      });
    }
    setValidationError(null);
  };

  const getAnswerDisplayText = (question) => {
    const answer = answers[question.question_id];
    if (!answer) return "Not answered";

    if (answer.type === "selection") {
      const option = question.options.find((o) => o.value === answer.value);
      return option?.label || answer.value;
    } else {
      return answer.value;
    }
  };

  const handleSubmit = () => {
    const parts = [];
    parts.push("**📋 User answers:**\n");
    for (let i = 0; i < questions.length; i++) {
      const question = questions[i];
      const answerText = getAnswerDisplayText(question);
      parts.push(`**${i + 1}. ${question.question_text}**`);
      parts.push(`   → ${answerText}\n`);
    }

    const formattedResponse = parts.join("\n");
    setIsExpanded?.(false);
    onSend(formattedResponse);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      const activeElement = document.activeElement;
      const isInputFocused =
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.isContentEditable);

      if (isInputFocused) return;

      if (e.key === "ArrowRight" || (e.key === "Tab" && !e.shiftKey)) {
        e.preventDefault();
        if (isInReviewMode) {
          // Already in review mode, do nothing
          return;
        }
        if (activeQuestionIndex < totalQuestions - 1) {
          setActiveQuestionIndex(activeQuestionIndex + 1);
        } else if (allQuestionsAnswered) {
          setIsInReviewMode(true);
        }
      } else if (e.key === "ArrowLeft" || (e.key === "Tab" && e.shiftKey)) {
        e.preventDefault();
        if (isInReviewMode) {
          setIsInReviewMode(false);
        } else if (activeQuestionIndex > 0) {
          setActiveQuestionIndex(activeQuestionIndex - 1);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    activeQuestionIndex,
    totalQuestions,
    isInReviewMode,
    allQuestionsAnswered,
  ]);

  if (!questions || questions.length === 0) return null;

  return (
    <View
      style={{
        flex: 1,
        flexDirection: "column",
        maxHeight: maxHeight,
      }}
    >
      {}
      <View
        style={{
          flexDirection: "row",
          height: 30,
          marginBottom: 5,
          gap: 4,
        }}
      >
        {questions.map((q, index) => (
          <div
            key={q.question_id}
            onClick={() => {
              setActiveQuestionIndex(index);
              setIsInReviewMode(false);
            }}
            style={{
              padding: "6px 12px",
              borderRadius: "6px 6px 0 0",
              cursor: "pointer",
              backgroundColor:
                activeQuestionIndex === index && !isInReviewMode
                  ? currentTheme?.theme?.primary
                  : currentTheme?.theme?.mediumGrey,
              color:
                activeQuestionIndex === index && !isInReviewMode
                  ? currentTheme?.theme?.white
                  : currentTheme?.theme?.font_color,
              fontWeight:
                activeQuestionIndex === index && !isInReviewMode
                  ? "600"
                  : "400",
              transition: "all 0.2s ease",
              border: answers[q.question_id]
                ? `1px solid ${currentTheme?.theme?.success}`
                : "none",
            }}
          >
            <AppText preventTranslation style={{ fontSize: 12 }}>
              Q{index + 1}
              {answers[q.question_id] && " ✓"}
            </AppText>
          </div>
        ))}

        {}
        <div
          onClick={() => {
            if (allQuestionsAnswered) {
              setIsInReviewMode(true);
            }
          }}
          style={{
            padding: "6px 12px",
            borderRadius: "6px 6px 0 0",
            cursor: allQuestionsAnswered ? "pointer" : "not-allowed",
            backgroundColor: isInReviewMode
              ? currentTheme?.theme?.primary
              : currentTheme?.theme?.mediumGrey,
            color: isInReviewMode
              ? currentTheme?.theme?.white
              : currentTheme?.theme?.font_color,
            opacity: allQuestionsAnswered ? 1 : 0.5,
            fontWeight: isInReviewMode ? "600" : "400",
          }}
        >
          <AppText preventTranslation style={{ fontSize: 12 }}>
            Review {allQuestionsAnswered && "✓"}
          </AppText>
        </div>
      </View>

      {}
      <View style={{ flex: 1, maxHeight: maxHeight - 80, overflowY: "auto" }}>
        {!isInReviewMode ? (
          <>
            {}
            <View style={{ marginBottom: 10 }}>
              <AppText
                style={{
                  fontSize: currentTheme?.theme?.medium_font_size,
                  fontWeight: "600",
                  marginBottom: 4,
                }}
                color={currentTheme?.theme?.font_color}
                preventTranslation
              >
                {questions[activeQuestionIndex]?.question_text}
              </AppText>
              {questions[activeQuestionIndex]?.question_context && (
                <AppText
                  style={{ fontSize: 12 }}
                  color={currentTheme?.theme?.darkGrey}
                  preventTranslation
                >
                  {questions[activeQuestionIndex]?.question_context}
                </AppText>
              )}
            </View>

            {}
            <View style={{ gap: 8, marginRight: 26 }}>
              {questions[activeQuestionIndex]?.options.map((option, idx) => (
                <div
                  key={option.value}
                  onClick={() => handleOptionSelect(option)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px 7px",
                    borderRadius: "8px",
                    border: `1px solid ${
                      answers[questions[activeQuestionIndex]?.question_id]
                        ?.value === option.value
                        ? currentTheme?.theme?.primary
                        : option.recommended
                        ? currentTheme?.theme?.primary_medium
                        : currentTheme?.theme?.border_color
                    }`,
                    backgroundColor:
                      answers[questions[activeQuestionIndex]?.question_id]
                        ?.value === option.value
                        ? currentTheme?.theme?.primary_soft
                        : "transparent",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  {}
                  <div
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      border: `1px solid ${
                        answers[questions[activeQuestionIndex]?.question_id]
                          ?.value === option.value
                          ? currentTheme?.theme?.primary
                          : currentTheme?.theme?.border_color
                      }`,
                      marginRight: 10,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {answers[questions[activeQuestionIndex]?.question_id]
                      ?.value === option.value && (
                      <div
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: currentTheme?.theme?.primary,
                        }}
                      />
                    )}
                  </div>

                  <AppText
                    style={{ flex: 1, fontSize: 11, fontWeight: "bold" }}
                    preventTranslation
                  >
                    {option.label}
                  </AppText>

                  {option.recommended && (
                    <span
                      style={{
                        marginLeft: 8,
                        padding: "2px 6px",
                        backgroundColor: currentTheme?.theme?.primary,
                        color: currentTheme?.theme?.white,
                        borderRadius: "4px",
                        fontSize: 11,
                        fontWeight: "600",
                        fontFamily: "normalFont",
                      }}
                    >
                      (Recommended)
                    </span>
                  )}
                  {option.description && (
                    <AppText
                      style={{
                        fontSize: 11,
                        marginLeft: 8,
                        color: currentTheme?.theme?.darkGrey,
                      }}
                      preventTranslation
                    >
                      {option.description}
                    </AppText>
                  )}
                </div>
              ))}

              {}
              {questions[activeQuestionIndex]?.allow_custom_input && (
                <div style={{ marginTop: 8 }}>
                  <AppText
                    style={{
                      fontSize: 12,
                      marginBottom: 4,
                      marginLeft: 3,
                    }}
                    color={currentTheme?.theme?.darkGrey}
                    preventTranslation
                  >
                    {t("chats.ui.orTypeOwnAnswer")}
                  </AppText>
                  <input
                    type="text"
                    placeholder="Type custom answer..."
                    value={
                      answers[questions[activeQuestionIndex]?.question_id]
                        ?.type === "custom"
                        ? answers[questions[activeQuestionIndex]?.question_id]
                            ?.value
                        : ""
                    }
                    onChange={(e) => handleCustomInput(e.target.value)}
                    style={{
                      width: "-webkit-fill-available",
                      padding: "10px 12px",

                      borderRadius: "8px",
                      border: `1px solid ${currentTheme?.theme?.border_color}`,
                      fontSize: 14,
                      fontFamily: "normalFont",
                      backgroundColor:
                        currentTheme?.theme?.inputBackgroundColor,
                      color: currentTheme?.theme?.font_color,
                    }}
                  />
                </div>
              )}
            </View>
          </>
        ) : (
          <>
            {}
            <AppText
              style={{
                marginRight: 25,

                marginBottom: 10,
              }}
              color={currentTheme?.theme?.font_color}
              preventTranslation
            >
              Review Your Answers:
            </AppText>

            {questions.map((q, idx) => (
              <View key={q.question_id} style={{ marginBottom: 10 }}>
                <AppText
                  style={{ fontSize: 14, fontWeight: "600" }}
                  color={currentTheme?.theme?.font_color}
                  preventTranslation
                >
                  {idx + 1}. {q.question_text}
                </AppText>
                <AppText
                  style={{
                    fontSize: 14,
                    marginLeft: 20,
                  }}
                  color={currentTheme?.theme?.primary}
                  preventTranslation
                >
                  → {getAnswerDisplayText(q)}
                </AppText>
              </View>
            ))}
          </>
        )}
      </View>

      {}
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          height: 40,
          paddingTop: 10,
        }}
      >
        {}
        <div
          onClick={() => {
            if (isInReviewMode) {
              setIsInReviewMode(false);
            } else if (activeQuestionIndex > 0) {
              setActiveQuestionIndex(activeQuestionIndex - 1);
            }
          }}
          style={{
            padding: "8px 16px",
            borderRadius: "6px",
            cursor: "pointer",
            backgroundColor: currentTheme?.theme?.primary,
            opacity: isInReviewMode || activeQuestionIndex > 0 ? 1 : 0.3,
            fontWeight: "500",
          }}
        >
          <AppText style={{ fontSize: 14 }} preventTranslation>
            ← Back
          </AppText>
        </div>

        {}
        {validationError && (
          <AppText
            style={{ fontSize: 12 }}
            color={currentTheme?.theme?.red}
            preventTranslation
          >
            {validationError}
          </AppText>
        )}

        {}
        <div
          onClick={() => {
            if (isInReviewMode) {
              handleSubmit();
            } else if (activeQuestionIndex < totalQuestions - 1) {
              setActiveQuestionIndex(activeQuestionIndex + 1);
            } else {
              if (allQuestionsAnswered) {
                setIsInReviewMode(true);
              } else {
                setValidationError(
                  "Please answer all questions before submitting",
                );
              }
            }
          }}
          style={{
            padding: "8px 16px",
            borderRadius: "6px",
            cursor: "pointer",
            backgroundColor: currentTheme?.theme?.primary,
            color: currentTheme?.theme?.white,
            fontWeight: "500",
          }}
        >
          <AppText style={{ fontSize: 14 }} preventTranslation>
            {isInReviewMode
              ? "Submit"
              : activeQuestionIndex < totalQuestions - 1
              ? "Next →"
              : "Review →"}
          </AppText>
        </div>
      </View>
    </View>
  );
};

export default StructuredQuestionsInput;
